import React, { useState } from 'react';
import { NavArrowDown, NavArrowUp, RefreshDouble } from 'iconoir-react';
import { useMatchExplanation, useRefreshMatchExplanation } from '../../hooks/useMatch';
import { api } from '../../utils';
import type { MatchExplanation as MatchExplanationType } from '../../types/match';
import MatchTierBadge from './MatchTierBadge';
import ReciprocityBadge from './ReciprocityBadge';
import { InfoTip } from '../common/InfoTip';
import { Tour } from '../common/Tour';

interface MatchExplanationProps {
  matchId: string;
  className?: string;
}

const MatchExplanation: React.FC<MatchExplanationProps> = ({ matchId, className = '' }) => {
  // Apr-25: was 3 layers (toggle → quick scan L2 toggle → full
  // analysis). Most users wouldn't double-click and the full analysis
  // (synergy / friction / talking points / score breakdown) was buried
  // 2 clicks deep. Flattened to 1 expansion: clicking "Why this match?"
  // now shows EVERYTHING at once. Trades vertical density for fewer
  // hidden surfaces — consistent with Rule 2.
  const [isExpanded, setIsExpanded] = useState(false);
  // Jun-16 (a user V1 test, X1): the expanded card rendered every section at
  // once ("essay no one will read"). Quick scan stays; the long-form detail
  // (full paragraph + synergy + challenges + topics + score breakdown) is
  // gated behind this second toggle.
  const [showFullAnalysis, setShowFullAnalysis] = useState(false);
  const { data, isLoading, error } = useMatchExplanation(matchId, isExpanded);
  const { mutate: refresh, isPending: isRefreshing } = useRefreshMatchExplanation();

  const explanation = data?.result as MatchExplanationType | undefined;

  const handleRefresh = (e: React.MouseEvent) => {
    e.stopPropagation();
    refresh(matchId);
  };

  // Apr-25: collapse the 3-layer accordion. Reset
  // showFullAnalysis state usage no longer needed; the single isExpanded
  // toggle now reveals all content (quick scan + headline + key points +
  // synergy + friction + talking points + score breakdown).
  // The previous "See full analysis" sub-toggle is removed.

  return (
    <div className={`bg-gray-light-25 rounded-xl border border-purple-25 ${className}`}>
      {/* Match-detail tour only fires once the explanation is expanded — its
          targets (match-detail-headline, match-detail-synergy, match-detail-
          start) don't exist in the DOM until then. Mounted inside the
          expanded branch so first-visit users see it right when the detail
          content appears. */}
      {isExpanded && <Tour name="match-detail" />}
      {/* Layer 1: Always visible — headline toggle */}
      <div className="w-full px-4 py-3 flex items-center justify-between rounded-xl">
        <button
          data-tour-id="match-why"
          onClick={() => {
            if (!isExpanded) {
              api.post('/analytics/track', {
                event_type: 'match_card_expanded',
                event_category: 'matching',
                event_data: { match_id: matchId },
              }).catch(() => {});
            }
            setIsExpanded(!isExpanded);
          }}
          className="flex items-center gap-2 hover:opacity-80 transition-opacity"
        >
          <span className="text-lg">💡</span>
          <span className="font-medium text-heading-500">Why this match?</span>
          {isExpanded ? (
            <NavArrowUp className="w-5 h-5 text-body-400" />
          ) : (
            <NavArrowDown className="w-5 h-5 text-body-400" />
          )}
        </button>
        {isExpanded && explanation && (
          <button
            onClick={handleRefresh}
            disabled={isRefreshing}
            className="p-1 hover:bg-gray-200 rounded-md transition-colors"
            title="Refresh explanation"
          >
            <RefreshDouble
              className={`w-4 h-4 text-body-400 ${isRefreshing ? 'animate-spin' : ''}`}
            />
          </button>
        )}
      </div>

      {isExpanded && (
        <div className="px-4 pb-4 border-t border-purple-25">
          {isLoading ? (
            <div className="py-4 flex items-center justify-center">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-500" />
              <span className="ml-2 text-sm text-body-400">Analyzing match...</span>
            </div>
          ) : error ? (
            <div className="py-4 text-center text-red-500 text-sm">
              Unable to load explanation. Please try again.
            </div>
          ) : explanation ? (
            <div className="pt-4 space-y-4">
              {/* Layer 2: Quick scan — tier, score, headline, key points */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3 flex-wrap">
                  <MatchTierBadge tier={explanation.match_tier} />
                  {/* Apr-17 Phase 2b: primary_goal reciprocity indicator */}
                  <ReciprocityBadge reciprocal={explanation.reciprocal} size='md' />
                  {explanation.generated_at && (
                    <span className="text-[11px] text-body-300">
                      Analyzed {new Date(explanation.generated_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
                    </span>
                  )}
                </div>
                <span className="text-2xl font-bold text-primary-500">
                  {explanation.compatibility_score}%
                </span>
              </div>

              {/* Headline */}
              {explanation.headline && (
                <p data-tour-id="match-detail-headline" className="text-sm font-semibold text-heading-600">
                  {explanation.headline}
                </p>
              )}

              {/* Key Points — scannable bullets */}
              {explanation.key_points?.length > 0 && (
                <ul className="space-y-1.5">
                  {explanation.key_points.map((point, idx) => (
                    // Neutral marker, deliberately. key_points is a flat list of
                    // strings with no polarity flag, and the AI service routinely
                    // returns disqualifiers in it: a live match showed "No
                    // confirmed budget, no consumer tech/SaaS fit" and
                    // "Vietnam-based RWA exchange, not your pre-seed/seed target
                    // profile" both wearing a green tick, under a headline
                    // reading "off your stated criteria". A green ✓ reads as
                    // "satisfied", so it was endorsing the reasons against the
                    // match. Classifying the text here would be keyword matching,
                    // which the matching pipeline forbids, so the marker states
                    // no opinion. Mirrors the mobile fix in match/[id].tsx.
                    <li key={idx} className="text-sm text-body-600 flex items-start gap-2">
                      <span className="text-body-400 mt-0.5 flex-shrink-0">•</span>
                      {point}
                    </li>
                  ))}
                </ul>
              )}

              {/* Quick stats: challenges + topics count */}
              <div className="flex items-center gap-4 text-xs text-body-400">
                {explanation.friction_points?.length > 0 && (
                  <span className="flex items-center gap-1">
                    <span>⚠️</span> {explanation.friction_points.length} potential challenge{explanation.friction_points.length > 1 ? 's' : ''}
                  </span>
                )}
                {explanation.talking_points?.length > 0 && (
                  <span className="flex items-center gap-1">
                    <span>💬</span> {explanation.talking_points.length} conversation starter{explanation.talking_points.length > 1 ? 's' : ''}
                  </span>
                )}
              </div>

              {/* Fallback: if no headline/key_points (old matches), show summary */}
              {!explanation.headline && !explanation.key_points?.length && (
                <p className="text-sm text-body-600 leading-relaxed">
                  {explanation.explanation}
                </p>
              )}

              {/* Jun-16 (a user V1 test, X1): the single expansion rendered the
                  full paragraph + synergy + challenges + topics + score
                  breakdown all at once — an unreadable "essay". The quick scan
                  above stays; this sub-toggle gates the deep detail. */}
              {(explanation.explanation ||
                explanation.synergy_areas?.length > 0 ||
                explanation.friction_points?.length > 0 ||
                explanation.talking_points?.length > 0) && (
                <button
                  onClick={() => setShowFullAnalysis(v => !v)}
                  className="text-xs font-medium text-primary-500 hover:opacity-80 flex items-center gap-1"
                >
                  {showFullAnalysis ? 'Show less' : 'See full analysis'}
                  {showFullAnalysis ? (
                    <NavArrowUp className="w-4 h-4" />
                  ) : (
                    <NavArrowDown className="w-4 h-4" />
                  )}
                </button>
              )}

              {showFullAnalysis && (
                <>
                  {/* Full explanation paragraph */}
                  {explanation.explanation && (
                    <p className="text-sm text-body-600 leading-relaxed pt-2 border-t border-gray-100">
                      {explanation.explanation}
                    </p>
                  )}

                  {/* Synergy Areas */}
                  {explanation.synergy_areas?.length > 0 && (
                    <div data-tour-id="match-detail-synergy">
                      <h4 className="text-sm font-semibold text-heading-500 mb-2 flex items-center gap-2">
                        <span>🤝</span> How You Can Help Each Other
                        <InfoTip tooltipKey="synergy-areas" />
                      </h4>
                      <ul className="space-y-1">
                        {explanation.synergy_areas.map((item, idx) => (
                          <li key={idx} className="text-sm text-body-600 flex items-start gap-2">
                            <span className="text-green-500 mt-0.5 flex-shrink-0">✓</span>
                            {item}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Friction Points */}
                  {explanation.friction_points?.length > 0 && (
                    <div>
                      <h4 className="text-sm font-semibold text-heading-500 mb-2 flex items-center gap-2">
                        <span>⚠️</span> Potential Challenges
                        <span className="text-xs text-body-400">({explanation.friction_points.length})</span>
                        <InfoTip tooltipKey="friction-points" />
                      </h4>
                      <ul className="space-y-1">
                        {explanation.friction_points.map((item, idx) => (
                          <li key={idx} className="text-sm text-body-500 flex items-start gap-2">
                            <span className="text-yellow-500 mt-0.5 flex-shrink-0">•</span>
                            {item}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Talking Points */}
                  {explanation.talking_points?.length > 0 && (
                    <div>
                      <h4 className="text-sm font-semibold text-heading-500 mb-2 flex items-center gap-2">
                        <span>💬</span> Suggested Topics to Discuss
                      </h4>
                      <ul className="space-y-2">
                        {explanation.talking_points.map((topic, idx) => (
                          <li
                            key={idx}
                            className="text-xs text-body-400 pl-3 border-l-2 border-primary-200"
                          >
                            {topic}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                </>
              )}
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
};

export default MatchExplanation;
